import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Percent, Tag, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiscountCodesManagement = () => {
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newCode, setNewCode] = useState({
    code: "",
    discount_percent: "",
    description: "",
    usage_limit: "",
    valid_from: "",
    valid_until: ""
  });

  useEffect(() => {
    fetchDiscountCodes();
  }, []);

  const fetchDiscountCodes = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/discount-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscountCodes(response.data);
    } catch (error) {
      toast.error("Villa við að sækja afsláttarkóða");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    
    if (!newCode.code || !newCode.discount_percent) {
      toast.error("Vinsamlegast fylltu út kóða og afsláttarprósentu");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/discount-codes`, {
        code: newCode.code,
        discount_percent: parseFloat(newCode.discount_percent),
        description: newCode.description || null,
        usage_limit: newCode.usage_limit ? parseInt(newCode.usage_limit) : null,
        valid_from: newCode.valid_from || null,
        valid_until: newCode.valid_until || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Afsláttarkóði búinn til!");
      setNewCode({
        code: "",
        discount_percent: "",
        description: "",
        usage_limit: "",
        valid_from: "",
        valid_until: ""
      });
      setShowForm(false);
      fetchDiscountCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Villa við að búa til kóða");
    }
  };

  const handleToggleActive = async (codeId, currentStatus) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/discount-codes/${codeId}?is_active=${!currentStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentStatus ? "Kóði óvirkjaður" : "Kóði virkjaður");
      fetchDiscountCodes();
    } catch (error) {
      toast.error("Villa við að uppfæra kóða");
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm("Ertu viss um að þú viljir eyða þessum kóða?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/discount-codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Kóða eytt!");
      fetchDiscountCodes();
    } catch (error) {
      toast.error("Villa við að eyða kóða");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Hleður...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-display font-bold">Afsláttarkóðar</h1>
            <p className="text-muted-foreground">Búðu til og stjórnaðu afsláttarkóðum</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)} data-testid="add-code-button">
            <Plus className="w-4 h-4 mr-2" />
            Nýr kóði
          </Button>
        </div>

        {/* Create new code form */}
        {showForm && (
          <div className="bg-white border rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Búa til nýjan afsláttarkóða
            </h2>
            <form onSubmit={handleCreateCode} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="code">Kóði *</Label>
                  <Input
                    id="code"
                    value={newCode.code}
                    onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                    placeholder="T.d. SUMAR2025"
                    className="uppercase"
                    data-testid="input-code"
                  />
                </div>
                <div>
                  <Label htmlFor="discount">Afsláttur (%) *</Label>
                  <div className="relative">
                    <Input
                      id="discount"
                      type="number"
                      min="1"
                      max="100"
                      value={newCode.discount_percent}
                      onChange={(e) => setNewCode({ ...newCode, discount_percent: e.target.value })}
                      placeholder="T.d. 10"
                      data-testid="input-discount"
                    />
                    <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Lýsing (valfrjálst)</Label>
                <Input
                  id="description"
                  value={newCode.description}
                  onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
                  placeholder="T.d. Sumartilboð fyrir endurtekna gesti"
                  data-testid="input-description"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="usage_limit">Notkunartakmörk</Label>
                  <Input
                    id="usage_limit"
                    type="number"
                    min="1"
                    value={newCode.usage_limit}
                    onChange={(e) => setNewCode({ ...newCode, usage_limit: e.target.value })}
                    placeholder="Ótakmarkað"
                    data-testid="input-usage-limit"
                  />
                </div>
                <div>
                  <Label htmlFor="valid_from">Gildir frá</Label>
                  <Input
                    id="valid_from"
                    type="date"
                    value={newCode.valid_from}
                    onChange={(e) => setNewCode({ ...newCode, valid_from: e.target.value })}
                    data-testid="input-valid-from"
                  />
                </div>
                <div>
                  <Label htmlFor="valid_until">Gildir til</Label>
                  <Input
                    id="valid_until"
                    type="date"
                    value={newCode.valid_until}
                    onChange={(e) => setNewCode({ ...newCode, valid_until: e.target.value })}
                    data-testid="input-valid-until"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" data-testid="submit-code-button">
                  Búa til kóða
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Hætta við
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Discount codes table */}
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          {discountCodes.length === 0 ? (
            <div className="p-12 text-center">
              <Tag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Engir afsláttarkóðar</h3>
              <p className="text-muted-foreground mb-4">Búðu til fyrsta afsláttarkóðann þinn</p>
              <Button onClick={() => setShowForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Búa til kóða
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kóði</TableHead>
                  <TableHead>Afsláttur</TableHead>
                  <TableHead>Lýsing</TableHead>
                  <TableHead>Notkun</TableHead>
                  <TableHead>Gildistími</TableHead>
                  <TableHead>Virkur</TableHead>
                  <TableHead className="text-right">Aðgerðir</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {discountCodes.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell>
                      <span className="font-mono font-bold text-primary-600 bg-primary-50 px-2 py-1 rounded">
                        {code.code}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold text-green-600">
                        {code.discount_percent}%
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {code.description || "-"}
                    </TableCell>
                    <TableCell>
                      {code.usage_count || 0}
                      {code.usage_limit ? ` / ${code.usage_limit}` : " / ∞"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {code.valid_from || code.valid_until ? (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          {code.valid_from || "..."} - {code.valid_until || "..."}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Alltaf gilt</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={code.is_active}
                        onCheckedChange={() => handleToggleActive(code.id, code.is_active)}
                        data-testid={`toggle-${code.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCode(code.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-${code.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default DiscountCodesManagement;
